import React, { useEffect, useRef } from 'react'
import {
	Appointment,
	Requisition,
	Comment,
	ClinicalEvaluation,
	FinalSituation,
	ClinicalEpisode,
	ProcessChangesHistory,
} from 'models'
import { useParams } from 'react-router-dom'

import { useFetchList } from 'store'

import RequisitionCard from 'components/cards/RequisitionCard/RequisitionCard'
import AppointmentCard, {
	AppointmentCardSkeleton,
} from 'components/cards/AppointmentCard/AppointmentCard'
import CommentCard from 'components/cards/CommentCard/CommentCard'
import ClinicalEvaluationCard from 'components/cards/ClinicalEvaluationCard/ClinicalEvaluationCard'
import FinalSituationCard from 'components/cards/FinalSituationCard/FinalSituationCard'
import EpisodesSeparatorCard from 'components/cards/EpisodesSeparatorCard/EpisodesSeparatorCard'
import MedicalOpinionTriageCard from 'components/cards/MedicalOpinionTriageCard/MedicalOpinionTriageCard'
import MedicalOpinionCard from 'components/cards/MedicalOpinionCard/MedicalOpinionCard'
import NoData from 'components/general/NoData/NoData'

import { calculateItemHeight, LINE_SPLIT_REGEX } from './utils'

import useRect from '@jimmyboys/liita-ui/dist/hooks/useRect'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useCalculateTextsHeights from '@jimmyboys/liita-ui/dist/hooks/useCalculateTextsHeights'
import Collection from '@jimmyboys/liita-ui/dist/components/Collection'
import { getScrollParentOffsetY } from '@jimmyboys/liita-ui/dist/utils/getScrollParent'
import { getElementOffset } from '@jimmyboys/liita-ui/dist/utils/getElementOffset'
import { useLocation } from 'react-use'

import themeStyles from 'assets/styles/theme.module.sass'
import useCollectionSpecs, {
	getThemeSpacing,
	getThemeVariable,
} from 'hooks/useCollectionSpecs'

import styles from './ClinicalProcess.module.sass'
import { useRecoilValue } from 'recoil'
import { currentDetailLevelState } from 'store/atoms'
import MedicalOpinion from 'models/MedicalOpinion'

const anchorType = { R: 'requisition', C: 'appointment' }
const { phi, smallFontSize } = themeStyles
const [themeVerticalSpacing, themeHorizontalSpacing] = getThemeSpacing()
const themeSmallFontSize = getThemeVariable(smallFontSize)

export const ClinicalHistoryController = () => {
	const collectionRef = useRef(null)
	const rect = useRect(collectionRef)
	const { i18n, screenSize } = useGlobal()
	const { clinicalProcessId } = useParams()
	const location = useLocation()
	const appointmentsUri = `/clinical_processes/${clinicalProcessId}/appointments`
	const requisitionsUri = `/clinical_processes/${clinicalProcessId}/requisitions`
	const commentsUri = `/clinical_processes/${clinicalProcessId}/comments`
	const clinicalEvaluationsUri = `/clinical_processes/${clinicalProcessId}/clinical_evaluations`
	const finalSituationsUri = `/clinical_processes/${clinicalProcessId}/final_situations`
	const clinicalEpisodesUri = `/clinical_processes/${clinicalProcessId}/clinical_episodes`
	const processChangesHistoryUri = `/clinical_processes/${clinicalProcessId}/history`
	const opinionsUri = `/clinical_processes/${clinicalProcessId}/medical_opinion`
	const anchorTag = location.hash.trim().slice(1).split('_')
	const [type, value] = anchorTag

	const detailLevel = useRecoilValue(currentDetailLevelState)

	const { state, contents } = useFetchList(
		[
			appointmentsUri,
			requisitionsUri,
			commentsUri,
			clinicalEvaluationsUri,
			finalSituationsUri,
			clinicalEpisodesUri,
			detailLevel === 'complete' && processChangesHistoryUri,
			opinionsUri,
		],
		[
			Appointment.fromJSON,
			Requisition.fromJSON,
			Comment.fromJSON,
			ClinicalEvaluation.fromJSON,
			FinalSituation.fromJSON,
			ClinicalEpisode.fromJSON,
			detailLevel === 'complete' && ProcessChangesHistory.fromJSON,
			MedicalOpinion.fromJSON,
		]
	)
	const isLoading = state === 'loading'

	const [
		appointmentsData,
		requisitionsData,
		commentsData,
		clinicalEvaluationsData,
		finalSituationData,
		clinicalEpisodesData = [],
		processChangesHistory = [],
		opinionsData,
	] = contents || []

	const cards = {
		AppointmentCardSkeleton,
		AppointmentCard,
		RequisitionCard,
		CommentCard,
		ClinicalEvaluationCard,
		FinalSituationCard,
		EpisodesSeparatorCard,
		MedicalOpinionCard,
		MedicalOpinionTriageCard,
	}

	const cardLabels = {
		doctor: i18n`Doctor`,
		diagnosis: i18n`Diagnosis`,
		disability: i18n`Disability`,
		dateAndPlace: i18n`Date/Place`,
		outsideNetwork: i18n`outside network`,
		place: i18n`Place`,
		medicalObservation: i18n`Medical Observation`,
		documents: i18n`Documents`,
		options: i18n`Options`,
		nullify: i18n`Nullify`,
		nullifyAppointment: i18n`Nullify Appointment`,
		nullifyMessagePart1: i18n`By nullify this appointment,`,
		nullifyMessagePart2Singular: i18n`requisition will be nullified.`,
		nullifyMessagePart2Plural: i18n`requisitions will be nullified.`,
		nullifyMessagePart3: i18n`If any appointment derive from this, will also be nullified.`,
		requisition: i18n`Requisition`,
		execution: i18n`Execution`,
		applicant: i18n`Applicant`,
		type: i18n`Type`,
		medicalActs: i18n`Medical Acts`,
		clinicalInformation: i18n`Clinical Information`,
		exams: i18n`Exams`,
		author: i18n`Author`,
		date: i18n`Date`,
		commentTitle: i18n`Commentary`,
		causalLink: i18n`Causal Link`,
		causalLinkDoubts: i18n`Causal Link Doubts`,
		clinicalEvaluation: i18n`Clinical Evaluation`,
		disabilityPrediction: i18n`Disability prediction`,
		yes: i18n`Yes`,
		no: i18n`No`,
		remainsTheSame: i18n`Remains the same`,
		notDefined: i18n`Not defined`,
		empty: i18n`Empty`,
		PTDOW: i18n`PTDOW`,
		additionalEfforts: i18n`Additional efforts`,
		PPD: i18n`PPD`,
		temp: i18n`Temp`,
		TTD: i18n`TTD`,
		biologicalDamage: i18n`Biological damage`,
		treatmentDays: i18n`Treatment days`,
		weight: i18n`Weight`,
		commentary: i18n`Commentary`,
		evaluation: i18n`Evaluation`,
		predication: i18n`Prediction`,
		current: i18n`Current`,
		causalDoubts: i18n`Causal Doubts`,
		processType: i18n`Process Type`,
		triage: i18n`Triage`,
		ippForecast: i18n`IPP Forecast`,
		dubious: i18n`Dubious`,
		changesHistory: i18n`Changes History`,
		requestedBy: i18n`Requested by`,
		toTheDateOf: i18n`to the date of`,
		medicalOpinion: i18n`Clinical opinion`,
	}

	const cardLayout = 'vertical'
	const appointmentsStatus = {}

	const textSpecs = useCollectionSpecs(
		collectionRef,
		rect,
		screenSize === 'small'
			? themeHorizontalSpacing * phi + (themeHorizontalSpacing / phi) * 5
			: themeHorizontalSpacing * phi * 6,
		themeSmallFontSize
	)

	processChangesHistory?.sort(
		(a, b) =>
			new Date(b.modificationDate.timestamp) -
			new Date(a.modificationDate.timestamp)
	)

	let groupedHistory = { appointments: [], requisitions: [] }
	if (detailLevel === 'complete') {
		groupedHistory = (processChangesHistory || []).reduce(
			(acc, elem) => {
				if (elem.requisitionId) {
					if (acc['requisitions'][elem.requisitionId] === undefined) {
						acc['requisitions'][elem.requisitionId] = []
					}
					acc['requisitions'][elem.requisitionId].push(elem)
				} else if (elem.appointmentId) {
					if (acc['appointments'][elem.appointmentId] === undefined) {
						acc['appointments'][elem.appointmentId] = []
					}
					acc['appointments'][elem.appointmentId].push(elem)
				}

				return acc
			},
			{ appointments: [], requisitions: [] }
		)
	}

	const requisitions = (requisitionsData || [])
		.map((json) => {
			return filterCancelledAppointments &&
				appointmentsStatus[json.appointmentId]?.status === 'cancelled'
				? null
				: {
						...json,
						history: groupedHistory['requisitions']?.[json.id],
						sortParam:
							json.executionDate?.timestamp ||
							json.scheduledDate?.timestamp ||
							json.requisitionDate?.timestamp,
						$indented: false,
						$cardName: 'RequisitionCard',
						$layout: cardLayout,
						$detailLevel: detailLevel,
						$cardLabels: cardLabels,
						$highlight:
							type === 'R' && value === json.id ? true : false,
				  }
		})
		.filter((r) => r)

	const filterCancelledAppointments = true
	const appointments = (appointmentsData || [])
		.map((json) => {
			appointmentsStatus[json.id] = {
				time: json.date.timestamp,
				status: json.status.value,
			}
			let appointment = {
				...json,
				processId: clinicalProcessId,
				history: groupedHistory['appointments']?.[json.id],
				requisitions: (requisitions || [])
					.filter(
						(requisition) => requisition.appointmentId === json.id
					)
					.map((elem) => ({
						...elem,
						$indented: true,
					}))
					.sort((a, b) => b.sortParam - a.sortParam),
				sortParam: json.date.timestamp,
				$cardName: 'AppointmentCard',
				$layout: cardLayout,
				$detailLevel: detailLevel,
				$highlight: type === 'C' && value === json.id ? true : false,
			}
			if (detailLevel === 'summary') {
				appointment = {
					...appointment,
					text:
						(json.disability?.rateStr
							? `${json.disability?.rateStr} - `
							: '') +
						(json.disability?.abbreviation || i18n`Missing`) +
						(json.currentDisability
							? ` / ${json.currentDisability.abbreviation}`
							: '') +
						', ' +
						json.medicalObservation.replace(
							/ *(\r\n|\n|\r)/gm,
							' '
						),
				}
			}

			return appointment
		})
		.filter(
			({ status }) =>
				!(filterCancelledAppointments && status?.value === 'cancelled')
		)

	// Some comments are the result of a clinical evaluation. We will filter them so there are no duplicates
	const clinicalEvaluationComments = {}
	const clinicalEvaluations = (clinicalEvaluationsData || []).map((json) => {
		const commentItem = (commentsData || []).find(({ author, date }) => {
			const deltaTime = date?.timestamp
				? Math.abs(date?.timestamp - json.date.timestamp)
				: 10000
			return (
				author?.id && author.id === json.doctor.id && deltaTime < 1000
			)
		})
		if (commentItem) {
			clinicalEvaluationComments[commentItem.id] = true
		}
		return {
			...json,
			body: json.body || commentItem?.body,
			text: json.body || commentItem?.body,
			sortParam: json.date.timestamp,
			$cardName: 'ClinicalEvaluationCard',
			$layout: cardLayout,
			$detailLevel: detailLevel,
		}
	})

	const comments = (commentsData || [])
		.filter(({ id, type }) => !clinicalEvaluationComments[id])
		.map((json) => {
			return {
				...json,
				sortParam: json.date.timestamp,
				$containerWidth:
					textSpecs.maxWidth - themeHorizontalSpacing * phi * phi,
				$cardName: 'CommentCard',
				$layout: cardLayout,
				$detailLevel: detailLevel,
			}
		})

	const finalSituations = (finalSituationData || []).map((json) => {
		return {
			...json,
			sortParam: json.date.timestamp,
			$cardName: 'FinalSituationCard',
			$layout: cardLayout,
			$detailLevel: detailLevel,
		}
	})

	const clinicalEpisodes =
		(appointments.length ||
			requisitions.length ||
			clinicalEvaluations.length ||
			comments.length ||
			finalSituations.length) &&
		clinicalEpisodesData
			? clinicalEpisodesData.slice(1).map((json, index) => {
					return {
						...json,
						prevEpisode: clinicalEpisodesData[index],
						sortParam: json.date.timestamp - 1001,
						$cardName: 'EpisodesSeparatorCard',
						$layout: cardLayout,
					}
			  })
			: []

	const medicalOpinions = (opinionsData || []).map((json) => {
		let cardName = ''
		switch (json.medicalObservationType) {
			case 'Triagem':
				cardName = 'MedicalOpinionTriageCard'
				break
			default:
				cardName = 'MedicalOpinionCard'
		}

		return {
			...json,
			sortParam:
				json.responseDate.timestamp || json.creationDate.timestamp,
			$cardName: cardName,
			$detailLevel: detailLevel,
		}
	})

	const data = [
		...appointments,
		...requisitions,
		...clinicalEvaluations,
		...finalSituations,
		...clinicalEpisodes,
		...comments,
		...medicalOpinions,
	].sort((a, b) => b.sortParam - a.sortParam)

	const cardMargin = themeVerticalSpacing / phi

	const isBeforeTextComputation = !(textSpecs.fontSize && textSpecs.maxWidth)

	const textsHeights = useCalculateTextsHeights(
		isBeforeTextComputation ? [] : [...data, ...requisitions],
		textSpecs.fontSize
			? `${textSpecs.fontWeight} ${textSpecs.fontSize}px ${
					textSpecs.fontFamily || ''
			  }`
			: null,
		textSpecs.letterSpacing,
		textSpecs.maxWidth,
		LINE_SPLIT_REGEX,
		screenSize
	)

	let collectionItems
	let itemsHeights
	if (isLoading) {
		const loadingItems = new Array(5).fill({})
		collectionItems = loadingItems.map(() => {
			return {
				$cardName: 'AppointmentCardSkeleton',
				$layout: cardLayout,
			}
		})
		itemsHeights = loadingItems.map(() => {
			return (
				AppointmentCard.defaultHeight +
				(parseFloat(styles.rowMargin) || cardMargin)
			)
		})
	} else {
		itemsHeights = []
		collectionItems = []
		data.forEach((item) => {
			const [height, textHeight] = calculateItemHeight(
				item,
				textsHeights,
				textSpecs.lineHeight,
				cardMargin,
				cards,
				detailLevel,
				screenSize
			)
			itemsHeights.push(height)
			collectionItems.push({
				...item,
				height,
				textHeight,
				$cardLabels: cardLabels,
			})
		})
	}

	useEffect(() => {
		if (anchorTag && collectionItems && itemsHeights) {
			const itemIndex = collectionItems.findIndex(
				(elem) => elem.key === `${anchorType[type]}_${value}`
			)

			if (itemIndex >= 0) {
				let itemScrollPos = 0
				for (let i = 0; i < itemIndex; i++) {
					itemScrollPos += itemsHeights[i]
				}

				const [, scrollTo] = getScrollParentOffsetY(
					collectionRef.current
				)

				let itemPos =
					itemScrollPos + getElementOffset(collectionRef.current).top

				scrollTo &&
					scrollTo(
						screenSize === 'small' ? itemPos : itemPos - 100,
						0,
						'smooth'
					)
			}
		}
	}, [
		anchorTag,
		collectionItems,
		itemsHeights,
		rect,
		screenSize,
		type,
		value,
	])

	return (
		<>
			{!isLoading && !collectionItems.length ? (
				<NoData
					applyMaxWidth
					paddings={['normal', 'normal']}
					message={i18n`This process has no extra data to show.`}
				/>
			) : null}
			<Collection
				className={`${styles['list']}`}
				ref={collectionRef}
				items={collectionItems}
				renderAhead={isBeforeTextComputation ? 0 : 10}
				itemsHeights={itemsHeights}
				cards={cards}
			/>
		</>
	)
}
